import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { SelectChangeEvent } from '@mui/material';
import { useMarketStatsFilter } from '../../../context';
import { AccountTypeValue, TOGGLE_OPTION_VALUES } from '../constants';
import { FilterOption } from '../types';

/**
 * Custom hook for managing GlobalFilters component state.
 * Reads and writes filter values to the shared MarketStatsFilterStore.
 * Toggle labels are resolved through i18n. Program and country options
 * are sourced exclusively from the API response stored in filterOptions —
 * their dropdowns remain hidden until the data loads.
 */
export const useGlobalFiltersState = () => {
  const store = useMarketStatsFilter();
  const { t } = useTranslation('communityStats');

  const toggleOptions: FilterOption<AccountTypeValue>[] = useMemo(
    () =>
      TOGGLE_OPTION_VALUES.map((value) => ({
        value,
        label: t(`globalFilters.${value}`),
      })),
    [t],
  );

  const programOptions: FilterOption[] = useMemo(() => {
    const allOption = {
      value: '',
      label: t('globalFilters.programPlaceholder'),
    };
    if (!store.filterOptions?.programs?.length) return [allOption];
    return [
      allOption,
      ...store.filterOptions.programs.map((p) => ({
        value: p.id,
        label: p.name,
      })),
    ];
  }, [store.filterOptions, t]);

  const countryOptions: FilterOption[] = useMemo(() => {
    if (!store.filterOptions?.countries?.length) return [];
    return [
      { value: '', label: t('globalFilters.countryPlaceholder') },
      ...store.filterOptions.countries.map((c) => ({
        value: c.value,
        label: c.label,
      })),
    ];
  }, [store.filterOptions, t]);

  const handleToggleChange = useCallback(
    (_: React.MouseEvent<HTMLElement>, value: string | null) => {
      if (value === null) return;
      store.setAccountType(value as AccountTypeValue);
    },
    [store],
  );

  const handleProgramChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const value = event.target.value as string;
      store.setProgramId(value || null);
    },
    [store],
  );

  const handleCountryChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const value = event.target.value as string;
      store.setCountry(value || null);
    },
    [store],
  );

  return {
    activeToggle: store.accountType,
    activeProgram: store.programId ?? '',
    activeCountry: store.country ?? '',
    toggleOptions,
    programOptions,
    countryOptions,
    handleToggleChange,
    handleProgramChange,
    handleCountryChange,
  };
};
